import { Flex, SearchBar } from '@brightdrop/bd-ui';
import { TranslatedLabel } from '@gm-commercial/translations';
import { Box, Divider, useMediaQuery, useTheme } from '@mui/material';

import { useEffect, useMemo, useRef, useState } from 'react';
import ShowHideColumnsComponent from '../ShowHideColumnsComponent/ShowHideColumnsComponent';
import {
  countArrayAttributeValues,
  FilterSummary,
  TableFilter,
} from '../TableFilter';
import type { TableToolbarProps } from './TableToolbar.model';
import useStyles, {
  TABLE_TOOLBAR_PADDING,
  TABLE_TOOLBAR_SEARCH_BAR_MARGIN,
  TABLE_TOOLBAR_SEARCH_BAR_WIDTH,
} from './TableToolbar.styles';

const TableToolbar = <T,>(props: TableToolbarProps<T>) => {
  const {
    countLabelConfig,
    searchBarConfig,
    filterConfig,
    showHideColumnsConfig,
    addDataButtonConfig,
  } = props;
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const theme = useTheme();
  const isTwoRowViewport = useMediaQuery(theme.breakpoints.down('lg'));

  const [currentFilterOptions, setCurrentFilterOptions] = useState<Partial<T>>(
    {}
  );
  const totalSelectedFilterCount = useMemo(
    () => countArrayAttributeValues(currentFilterOptions),
    [currentFilterOptions]
  );

  /* Sync currentFilterOptions with filterConfig.entity when it changes */
  useEffect(() => {
    if (filterConfig?.entity !== undefined) {
      setCurrentFilterOptions(filterConfig.entity);
    }
  }, [filterConfig?.entity]);

  /* Refs and state for tracking toolbar container and column widths */
  const leftColumnContainerRef = useRef<HTMLDivElement>(null);
  const [leftColumnWidth, setLeftColumnWidth] = useState<number>(0);
  const rightColumnContainerRef = useRef<HTMLDivElement>(null);
  const [rightColumnWidth, setRightColumnWidth] = useState<number>(0);
  const toolbarContainerRef = useRef<HTMLDivElement>(null);
  const [toolbarWidth, setToolbarWidth] = useState<number>(0);

  /* Observe toolbar container and columns for width changes */
  useEffect(() => {
    const toolbarContainer = toolbarContainerRef.current;
    const leftColumnContainer = leftColumnContainerRef.current;
    const rightColumnContainer = rightColumnContainerRef.current;
    if (!toolbarContainer || !leftColumnContainer || !rightColumnContainer)
      return;

    const toolbarContainerObserver = new ResizeObserver((entries) => {
      setToolbarWidth(entries[0].contentRect.width);
    });
    const leftColumnContainerObserver = new ResizeObserver((entries) => {
      setLeftColumnWidth(entries[0].contentRect.width);
    });
    const rightColumnContainerObserver = new ResizeObserver((entries) => {
      setRightColumnWidth(
        entries[0].contentRect.width +
          (searchBarConfig
            ? TABLE_TOOLBAR_SEARCH_BAR_WIDTH +
              2 * TABLE_TOOLBAR_PADDING +
              2 * TABLE_TOOLBAR_SEARCH_BAR_MARGIN
            : 0)
      );
    });

    toolbarContainerObserver.observe(toolbarContainer);
    leftColumnContainerObserver.observe(leftColumnContainer);
    rightColumnContainerObserver.observe(rightColumnContainer);
    return () => {
      toolbarContainerObserver.disconnect();
      leftColumnContainerObserver.disconnect();
      rightColumnContainerObserver.disconnect();
    };
  }, [searchBarConfig]);

  /* Use calculated widths and current viewport size to determine if the toolbar should be displayed as two rows */
  const showAsTwoRows = useMemo(() => {
    return (
      isTwoRowViewport || leftColumnWidth + rightColumnWidth > toolbarWidth
    );
  }, [leftColumnWidth, rightColumnWidth, toolbarWidth, isTwoRowViewport]);

  const { classes } = useStyles({
    showAsTwoRows: showAsTwoRows,
    isTwoRowViewport: isTwoRowViewport,
  });

  return (
    <Box ref={toolbarContainerRef}>
      <Flex
        col={showAsTwoRows}
        className={classes.toolbarContainer}
        dataTestId="table-toolbar-container"
      >
        <Box
          className={classes.leftColumnContainer}
          ref={leftColumnContainerRef}
        >
          <Flex col style={{ width: 'max-content' }}>
            <TranslatedLabel
              translationKey={countLabelConfig.translationKey}
              typographyVariant="body_medium"
              variables={countLabelConfig.variables}
              dataTestId={
                countLabelConfig.dataTestId ?? 'table-total-count-label'
              }
            />
            {filterConfig && totalSelectedFilterCount > 0 && (
              <Flex
                style={{
                  flexWrap: 'wrap',
                  maxWidth: `${toolbarWidth - 2 * TABLE_TOOLBAR_PADDING}px`,
                  alignItems: 'center',
                }}
                gap={4}
              >
                <FilterSummary
                  currentFilterOptions={currentFilterOptions}
                  getChipLabel={filterConfig.getChipLabel}
                  onFilterOptionsChange={(options: Partial<T>) => {
                    setCurrentFilterOptions(options);
                    filterConfig.saveFilterOptions(options);
                  }}
                />
              </Flex>
            )}
          </Flex>
        </Box>

        <Flex
          className={classes.rightColumnContainer}
          gap={TABLE_TOOLBAR_SEARCH_BAR_MARGIN}
        >
          {searchBarConfig &&
            !(isTwoRowViewport && searchBarConfig.hideOnMobile) && (
              <Box
                sx={{
                  width: showAsTwoRows
                    ? '100%'
                    : `${TABLE_TOOLBAR_SEARCH_BAR_WIDTH}px`,
                }}
              >
                <SearchBar {...searchBarConfig} isFullWidthSearchBar />
              </Box>
            )}

          <Box ref={rightColumnContainerRef} display="flex" gap={'6px'}>
            {filterConfig &&
              !(isTwoRowViewport && filterConfig.hideOnMobile) && (
                <Box width="max-content">
                  <TableFilter
                    {...filterConfig}
                    modalOpen={modalOpen}
                    onModalOpenUpdate={setModalOpen}
                    saveFilterOptions={(options: Partial<T>) => {
                      setCurrentFilterOptions(options);
                      filterConfig.saveFilterOptions(options);
                    }}
                  />
                </Box>
              )}
            {showHideColumnsConfig &&
              !(isTwoRowViewport && showHideColumnsConfig.hideOnMobile) && (
                <>
                  {filterConfig && (
                    <Divider
                      orientation="vertical"
                      flexItem
                      className={classes.verticalDivider}
                    />
                  )}
                  <ShowHideColumnsComponent {...showHideColumnsConfig} />
                </>
              )}
            {addDataButtonConfig &&
              !(isTwoRowViewport && addDataButtonConfig.hideOnMobile) && (
                <>
                  {(filterConfig || showHideColumnsConfig) && (
                    <Divider
                      orientation="vertical"
                      flexItem
                      className={classes.verticalDivider}
                    />
                  )}
                  <Box width="max-content">{addDataButtonConfig.component}</Box>
                </>
              )}
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
};

export default TableToolbar;
